/**
 * Auth Service
 * Handles authentication-related API calls
 */

import api from '@/lib/axios'
import { logger } from '@/lib/logger'
import { tokenManager } from '@/utils/tokenManager'
import { navigationService } from '@/services/api/shared/navigation.service'
import { clearStoredUIPreferences } from '@/utils/ui-preferences-storage.util'
import { API_ENDPOINTS } from '@/config/api.constants'
import { store } from '@/app/store'
import { setPermissions, resetPermissions } from '@/store/slices/permissions.slice'
import { resetAuthState, setUser, workspaceSessionReset } from '@/store/slices/auth.slice'
import { resetNavigation } from '@/store/slices/navigation.slice'
import { emitAuthBroadcastIfSupported } from '@/lib/session/auth-broadcast'
import { clearCachedData, getCacheKey } from '@/utils/localStorageCache.util'
import type { IdentityProfileType, User as ReduxAuthUser } from '@/types/shared/auth.types'

export interface LoginCredentials {
  username: string // Backend expects 'username' which can be email or phone
  password: string
}

export interface UserContext {
  type: 'PLATFORM' | 'ORGANIZATION' | 'BRANCH' | 'SELF' | 'ALUMNI'
  orgId?: string
  orgName?: string
  orgSlug?: string
  branchId?: string
  branchName?: string
  branchSlug?: string
}

export interface LoginResponse {
  accessToken: string
  refreshToken?: string // Optional - stored in httpOnly cookie by backend
  user: User
  contexts?: UserContext[]
  hasOrganizations?: boolean
  hasPendingInvites?: boolean
  pendingInvitesCount?: number
  onboarding?: {
    resumeRequired: boolean
    resumeStepSlug: 'account-setup' | 'organization-profile' | 'branch-setup' | 'plan-selection' | 'payment-launch' | null
    resumeReason?: string
  }
  /** Theme, mode, font size from backend; save globally and apply on first login */
  uiPreferences?: import('@/services/api/shared/ui-preferences.service').UIPreferences | null
}

export interface User {
  id: string
  email: string
  name: string
  userType: string
  isActive: boolean
  roles?: any[]
}

export interface ContextSelectionData {
  contextType: 'platform' | 'organization' | 'branch' // Backend expects lowercase
  orgSlug?: string // Internal frontend field
  organizationSlug?: string // Backend API field
  branchSlug?: string
  /** Phase 2: Switch profile within same branch; only use backend-exposed profile types */
  profileType?: IdentityProfileType
  activeProfileId?: string
}


class AuthService {
  /** Map partial login/session user payloads into Redux `User` shape (strict fields). */
  private normalizeFreshLoginReduxUser(
    incoming: Partial<ReduxAuthUser> &
      Pick<ReduxAuthUser, 'id' | 'email'> & {
        name?: string
      }
  ): ReduxAuthUser {
    const fallbackTime = new Date().toISOString()
    const display =
      incoming.fullName?.trim() ||
      incoming.name?.trim() ||
      [incoming.firstName, incoming.lastName].filter(Boolean).join(' ').trim() ||
      incoming.email

    return {
      id: incoming.id,
      email: incoming.email,
      firstName: incoming.firstName,
      lastName: incoming.lastName,
      fullName: display,
      phone: incoming.phone,
      avatar: incoming.avatar,
      userType: incoming.userType ?? ('SELF' as ReduxAuthUser['userType']),
      roles: incoming.roles,
      organizationId: incoming.organizationId,
      organizationSlug: incoming.organizationSlug,
      branchId: incoming.branchId,
      branchSlug: incoming.branchSlug,
      isEmailVerified: incoming.isEmailVerified ?? false,
      isPhoneVerified: incoming.isPhoneVerified ?? false,
      isActive: incoming.isActive ?? true,
      createdAt: incoming.createdAt ?? fallbackTime,
      updatedAt: incoming.updatedAt ?? fallbackTime,
    }
  }

  /** Temporary diagnostics — dev builds only (remove or gate further when stable). */
  private logAuthDiag(label: string, payload: Record<string, unknown>): void {
    try {
      logger.info(`[Auth] ${label}`, payload)
    } catch {
      /* noop */
    }
  }

  /**
   * Wipe stale workspace client state immediately after successful authentication — before select-context /
   * redirect — so menus, PCS-style artifacts, or anonymous user-context cache cannot bleed across accounts.
   */
  prepareFreshLoginSession(user: User | ReduxAuthUser): void {
    try {
      clearCachedData(getCacheKey('userContext'))
      clearCachedData()
      navigationService.clearStoredSidebarMenu()
    } catch {
      /* noop */
    }

    tokenManager.removeToken('contextToken')

    try {
      sessionStorage.removeItem('skipNextWorkspaceBootstrap')
      sessionStorage.removeItem('skipBootstrapForToken')
      sessionStorage.removeItem('contextSelectionInProgress')
      sessionStorage.removeItem('logoutInProgress')
      sessionStorage.removeItem('logoutApiCalled')
      sessionStorage.removeItem('currentOrgSlug')
      sessionStorage.removeItem('currentBranchSlug')
    } catch {
      /* noop */
    }

    try {
      store.dispatch(workspaceSessionReset())
      store.dispatch(resetPermissions())
      store.dispatch(resetNavigation())
      const reduxCandidate = user as Partial<ReduxAuthUser> & Pick<ReduxAuthUser, 'id' | 'email'> & { name?: string }
      store.dispatch(setUser(this.normalizeFreshLoginReduxUser(reduxCandidate)))
    } catch {
      /* noop */
    }

    this.logAuthDiag('prepareFreshLoginSession', {
      loggedInEmail: user?.email ?? null,
      userId: user?.id ?? null,
    })
  }

  /** Log issuance of workspace context JWT (omit token value — sensitive). */
  logContextIssued(meta: { userId?: string; email?: string; contextTokenFingerprint: string }): void {
    this.logAuthDiag('contextToken issued', {
      userId: meta.userId ?? null,
      loggedInEmail: meta.email ?? null,
      contextTokenFingerprint: meta.contextTokenFingerprint,
    })
  }

  private readonly USER_KEY = 'user'
  private readonly CONTEXT_KEY = 'context'

  /**
   * Login with username (email or phone) and password
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await api.post(API_ENDPOINTS.AUTH.LOGIN, credentials)
    const { user } = response.data.data
    
    // accessToken is now returned in response body (for cross-subdomain navigation)
    // refreshToken remains httpOnly-only for security
    // Backend sets both in httpOnly cookies AND returns accessToken in response body
    const accessToken = response.data.data?.tokens?.accessToken || ''
    if (accessToken) {
      tokenManager.setToken('accessToken', accessToken)
    }

    // Extract organization/invite flags and UI preferences from response
    const hasOrganizations = response.data.data.hasOrganizations ?? false
    const hasPendingInvites = response.data.data.hasPendingInvites ?? false
    const pendingInvitesCount = response.data.data.pendingInvitesCount ?? 0
    const onboarding = response.data.data.onboarding ?? undefined
    const uiPreferences = response.data.data.uiPreferences ?? null

    // NOTE: Tokens are NOT stored here - they will be stored after navigation completes
    // This keeps tokens in memory (Redux state) during login flow
    // refreshToken and accessToken are stored in httpOnly cookies by backend
    // accessToken is also returned in response body for cross-subdomain navigation
    
    // Note: Contexts are fetched separately via /navigation/user-context endpoint
    // The /api/auth/contexts endpoint doesn't exist in the backend
    // Contexts will be fetched in LoginPage via navigationService.getUserContext()
    
    return {
      accessToken,
      user,
      contexts: undefined, // Will be fetched from /navigation/user-context
      hasOrganizations,
      hasPendingInvites,
      pendingInvitesCount,
      onboarding,
      uiPreferences: uiPreferences ?? undefined,
    }
  }

  /**
   * Request OTP for passwordless login
   * Backend expects 'username' field (can be email or phone)
   */
  async requestOTP(username: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(API_ENDPOINTS.AUTH.REQUEST_OTP, { username })
    return response.data
  }

  /**
   * Login with OTP (for login flow)
   */
  async loginWithOTP(username: string, otp: string): Promise<LoginResponse> {
    const response = await api.post(API_ENDPOINTS.AUTH.LOGIN_WITH_OTP, { username, otp })
    const { user } = response.data.data
    
    // accessToken is now returned in response body (for cross-subdomain navigation)
    // refreshToken remains httpOnly-only for security
    // Backend sets both in httpOnly cookies AND returns accessToken in response body
    const accessToken = response.data.data?.tokens?.accessToken || ''
    if (accessToken) {
      tokenManager.setToken('accessToken', accessToken)
    }

    // Extract organization/invite flags from response
    const hasOrganizations = response.data.data.hasOrganizations ?? false
    const hasPendingInvites = response.data.data.hasPendingInvites ?? false
    const pendingInvitesCount = response.data.data.pendingInvitesCount ?? 0
    const onboarding = response.data.data.onboarding ?? undefined

    // NOTE: Tokens are NOT stored here - they will be stored after navigation completes
    // This keeps tokens in memory (Redux state) during login flow
    // refreshToken and accessToken are stored in httpOnly cookies by backend
    // accessToken is also returned in response body for cross-subdomain navigation
    
    // Note: Contexts are fetched separately via /navigation/user-context endpoint
    // The /api/auth/contexts endpoint doesn't exist in the backend
    // Contexts will be fetched in LoginPage via navigationService.getUserContext()
    
    return {
      accessToken,
      user,
      contexts: undefined, // Will be fetched from /navigation/user-context
      hasOrganizations,
      hasPendingInvites,
      pendingInvitesCount,
      onboarding,
    }
  }

  /**
   * Verify OTP and login (alternative endpoint)
   */
  async verifyOTP(email: string, otp: string): Promise<LoginResponse> {
    const response = await api.post(API_ENDPOINTS.AUTH.VERIFY_OTP, { email, otp })
    const { user } = response.data.data
    
    // accessToken and refreshToken are in httpOnly cookies, not in response body
    // Backend removes them from response for security
    // Return empty string for accessToken since it's not accessible via JavaScript
    const accessToken = ''

    // NOTE: Tokens are NOT stored here - they will be stored after navigation completes
    // This keeps tokens in memory (Redux state) during login flow
    // refreshToken and accessToken are stored in httpOnly cookies by backend
    
    // Note: Contexts are fetched separately via /navigation/user-context endpoint
    // The /api/auth/contexts endpoint doesn't exist in the backend
    // Contexts will be fetched in LoginPage via navigationService.getUserContext()
    
    return {
      accessToken,
      user,
      contexts: undefined, // Will be fetched from /navigation/user-context
    }
  }

  /**
   * Request OTP for password reset (forgot password)
   * When user not found: backend returns { success, message } with generic message, no data.
   * When user found: backend returns { success, message, data: { email, expiresIn } }.
   */
  async requestPasswordResetOTP(username: string): Promise<{
    success: boolean
    message: string
    data?: { email?: string; expiresIn?: string }
  }> {
    const response = await api.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { username })
    return response.data
  }

  /**
   * Reset password with OTP
   */
  async resetPassword(data: {
    username: string
    otp: string
    newPassword: string
    confirmPassword: string
  }): Promise<{ success: boolean; message: string }> {
    const response = await api.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, data)
    return response.data
  }

  /**
   * Refresh access token
   * Note: refreshToken is now in httpOnly cookie, sent automatically
   */
  async refreshToken(): Promise<string> {
    // refreshToken is in httpOnly cookie, sent automatically by browser
    await api.post(API_ENDPOINTS.AUTH.REFRESH_TOKEN, {})
    
    // accessToken is now also in httpOnly cookie, set by backend
    // Backend removes it from response body for security
    // We can't return it since it's not accessible via JavaScript
    // Return empty string to indicate success (token is in cookie)
    return ''
  }

  /**
   * Get available contexts for user
   * Note: This uses /navigation/user-context endpoint, not /auth/contexts
   */
  async getContexts(): Promise<UserContext[]> {
    try {
      // Use a fresh fetch here to avoid stale "no workspaces" cache
      // right after logout/login or cross-domain session transitions.
      const userContextResponse = await navigationService.getUserContext(true)
      const accessibleDomains = userContextResponse?.domainNavigation?.accessibleDomains || []
      
      // Transform domains to UserContext format
      const contexts = navigationService.transformDomainsToContexts(accessibleDomains)
      this.setContexts(contexts)
      return contexts
    } catch (error) {
      return []
    }
  }

  /**
   * Select context (organization/branch).
   * For parents with multiple linked students, backend may return requiresStudentSelection instead of a token.
   * For users with both employee and parent access in the same branch, backend returns requiresProfileSelection (e.g. teacher + parent).
   */
  async selectContext(data: ContextSelectionData): Promise<
    | { contextToken: string; domainNavigation?: { selectedContext?: { defaultURL?: string } }; contextData?: UserContext; uiPreferences?: import('@/services/api/shared/ui-preferences.service').UIPreferences | null }
    | { requiresStudentSelection: true; linkedStudents: Array<{ id: string; firstName?: string; lastName?: string }>; message?: string }
    | {
        requiresProfileSelection: true
        availableProfiles: Array<{ profileType: string; profileId?: string; designation?: string }>
        message?: string
      }
  > {
    // Map frontend field names to backend API field names
    // Backend expects organizationSlug, not orgSlug
    const organizationSlug = data.organizationSlug || data.orgSlug
    const requestBody: {
      contextType: 'platform' | 'organization' | 'branch'
      organizationSlug?: string
      branchSlug?: string
    } = {
      contextType: data.contextType,
    }
    
    // Only include organizationSlug if it exists (required for organization/branch contexts)
    if (organizationSlug) {
      requestBody.organizationSlug = organizationSlug
    }
    
    // Only include branchSlug if it exists (required for branch contexts)
    if (data.branchSlug) {
      requestBody.branchSlug = data.branchSlug
    }
    if (data.profileType) {
      (requestBody as any).profileType = data.profileType
    }
    if (data.activeProfileId) {
      (requestBody as any).activeProfileId = data.activeProfileId
    }

    // This request uses access token (handled by axios interceptor)
    // Increase timeout for select-context as it may take longer (menu generation, etc.)
    const response = await api.post(API_ENDPOINTS.AUTH.SELECT_CONTEXT, requestBody, {
      timeout: 120000, // 120 seconds (2 minutes) timeout for context selection - backend may take time for menu generation
    })
    // Handle different response structures
    // Backend might return: { success: true, data: { contextToken, ... } } or { contextToken, ... }
    let responseData = response.data
    
    // Check if response has nested data structure
    if (response.data?.data) {
      responseData = response.data.data
    }
    
    if (!responseData) {
      throw new Error('Invalid response structure: missing data')
    }

    // Parent with multiple students: backend returns requiresStudentSelection and linkedStudents (no token yet)
    if (responseData.requiresStudentSelection === true) {
      return {
        requiresStudentSelection: true,
        linkedStudents: responseData.linkedStudents || [],
        message: responseData.message,
      }
    }

    // Teacher + parent (or multiple branch roles): backend returns profile list, no token until user picks a role
    if (responseData.requiresProfileSelection === true) {
      return {
        requiresProfileSelection: true,
        availableProfiles: responseData.availableProfiles || [],
        message: responseData.message,
      }
    }

    const { contextToken, domainNavigation } = responseData

    if (!contextToken) {
      throw new Error('No context token received from server')
    }
    // NOTE: Context token and data are NOT stored here - they will be stored after navigation completes
    // This keeps tokens in memory (Redux state) during login flow

    // Build context data - try to get names from domainNavigation if available
    // Convert backend lowercase type back to frontend uppercase type for storage
    const typeMap: Record<string, UserContext['type']> = {
      'platform': 'PLATFORM',
      'organization': 'ORGANIZATION',
      'branch': 'BRANCH',
    }
    
    const orgSlug = data.organizationSlug || data.orgSlug
    
    // Build context data - try to get names from domainNavigation if available
    let orgName: string | undefined
    let branchName: string | undefined
    
    // Try to extract names from domainNavigation.selectedContext if available
    if (domainNavigation?.selectedContext) {
      const selectedContext = domainNavigation.selectedContext
      // The selectedContext might have name information
      if (selectedContext.name) {
        if (data.contextType === 'branch') {
          // For branch: "Branch Name - Org Name" format
          const parts = selectedContext.name.split(' - ')
          if (parts.length >= 2) {
            branchName = parts[0]
            orgName = parts[1]
          } else {
            branchName = selectedContext.name
          }
        } else if (data.contextType === 'organization') {
          orgName = selectedContext.name
        }
      }
    }
    
    // Build context data with all available information
    // This will be returned and stored later after navigation
    const contextData: UserContext = {
      type: typeMap[data.contextType] || 'PLATFORM',
      orgSlug: orgSlug,
      branchSlug: data.branchSlug,
      orgName: orgName,
      branchName: branchName,
    }

    // Return contextToken, contextData, and uiPreferences for storage/theme
    return {
      contextToken,
      domainNavigation: domainNavigation || undefined,
      contextData,
      uiPreferences: responseData.uiPreferences ?? undefined,
    }
  }

  /**
   * Select student context (parent with multiple linked students).
   * Call after select-context returns requiresStudentSelection: true.
   * Validates that studentProfileId is linked to the current user for the given branch.
   */
  async selectStudentContext(
    organizationSlug: string,
    branchSlug: string,
    studentProfileId: string
  ): Promise<{
    contextToken: string
    domainNavigation?: { selectedContext?: { defaultURL?: string } }
    contextData?: UserContext
    uiPreferences?: import('@/services/api/shared/ui-preferences.service').UIPreferences | null
  }> {
    const response = await api.post(
      API_ENDPOINTS.AUTH.SELECT_STUDENT_CONTEXT,
      { organizationSlug, branchSlug, studentProfileId },
      { timeout: 60000 }
    )
    const responseData = response.data?.data ?? response.data
    if (!responseData?.contextToken) {
      throw new Error(responseData?.message || 'No context token received from server')
    }
    const contextData: UserContext = {
      type: 'BRANCH',
      orgSlug: organizationSlug,
      branchSlug,
    }
    return {
      contextToken: responseData.contextToken,
      domainNavigation: responseData.domainNavigation,
      contextData,
      uiPreferences: responseData.uiPreferences ?? undefined,
    }
  }

  /**
   * Get full context data from cache
   * Returns branch_ids, roles, permissions that were moved out of JWT
   * This prevents 431 "Request Header Fields Too Large" errors
   * 
   * Available for ALL context types (platform, organization, branch)
   * 
   * Call this after select-context to get the full context data
   */
  async getContextData(): Promise<{
    branch_ids: string[]
    active_branch_id: string
    roles: { [branchId: string]: string }
    permissions: { [branchId: string]: string[] }
    contextType: 'platform' | 'organization' | 'branch'
    organizationId?: string
    branchId?: string
    organizationSlug?: string
    branchSlug?: string
    profileType?: IdentityProfileType
    activeProfileId?: string
  }> {
    // This request uses context token (handled by axios interceptor)
    const response = await api.get(API_ENDPOINTS.AUTH.GET_CONTEXT, {
      timeout: 30000, // 30 seconds timeout
    })
    // Handle different response structures
    let responseData = response.data
    if (response.data?.data) {
      responseData = response.data.data
    }
    
    if (!responseData) {
      throw new Error('Invalid response structure: missing data')
    }
    return responseData
  }

  /**
   * Switch active branch within the same organization context
   * POST /api/auth/switch-branch
   * 
   * @param branchId - Branch ID to switch to
   * @returns New context token with updated active_branch_id
   */
  async switchBranch(branchId: string): Promise<{ contextToken: string }> {
    // Use obfuscated token for header so copied value is unusable elsewhere
    const contextTokenForRequest = tokenManager.getTokenForRequest('contextToken')
    
    if (!contextTokenForRequest) {
      throw new Error('No context token found. Please select a context first.')
    }
    // Call switch-branch API with context token in Authorization header
    const response = await api.post(
      API_ENDPOINTS.AUTH.SWITCH_BRANCH,
      { branchId },
      {
        headers: {
          Authorization: `Bearer ${contextTokenForRequest}`
        },
        timeout: 30000 // 30 seconds timeout
      }
    )
    // Handle different response structures
    let responseData = response.data
    if (response.data?.data) {
      responseData = response.data.data
    }

    if (!responseData || !responseData.contextToken) {
      throw new Error('No context token received from server')
    }

    const newContextToken = responseData.contextToken
    // Update context token in storage
    tokenManager.setToken('contextToken', newContextToken, { expires: 7 })

    // Return new context token
    return {
      contextToken: newContextToken
    }
  }

  /**
   * Get stored contexts from sessionStorage
   */
  getStoredContexts(): UserContext[] {
    // Try sessionStorage first
    const contextsStr = sessionStorage.getItem('contexts')
    if (contextsStr) {
      return JSON.parse(contextsStr)
    }
    
    // Migration: check localStorage
    try {
      const localStorageContextsStr = localStorage.getItem('contexts')
      if (localStorageContextsStr) {
        const contexts = JSON.parse(localStorageContextsStr)
        // Migrate to sessionStorage
        sessionStorage.setItem('contexts', localStorageContextsStr)
        // Remove from localStorage
        localStorage.removeItem('contexts')
        return contexts
      }
    } catch (migrationError) {
      // Silent error handling
    }
    
    return []
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await api.post(API_ENDPOINTS.AUTH.LOGOUT)
    } catch (error) {
      // Ignore errors during logout
    } finally {
      this.clearAuth()
      emitAuthBroadcastIfSupported({ type: 'logout' })
    }
  }

  /**
   * Logout from all devices
   */
  async logoutAllDevices(): Promise<void> {
    try {
      await api.post(API_ENDPOINTS.AUTH.LOGOUT_ALL_DEVICES)
    } catch (error) {
    } finally {
      this.clearAuth()
    }
  }

  /**
   * Store tokens after navigation completes
   * This is called after navigation to store all tokens that were kept in memory during login flow
   * Note: accessToken and refreshToken are in httpOnly cookies, not stored here
   */
  storeTokensAfterNavigation(_accessToken: string, _refreshToken: string | undefined, contextToken: string): void {
    // accessToken and refreshToken are in httpOnly cookies, set by backend
    // Only store contextToken in sessionStorage
    if (contextToken) {
      tokenManager.setToken('contextToken', contextToken, { expires: 7 })
    }
  }

  /**
   * Get UI permissions based on context token
   * Returns plain JSON permissions array (not JWT token)
   * POST /api/auth/ui-permissions
   * 
   * @param contextToken - Optional context token to use (if not provided, uses token from storage/interceptor)
   * @param skipStorage - If true, don't store the permissions (for login flow)
   */
  async getUIPermissions(contextToken?: string, skipStorage: boolean = false): Promise<{ 
    permissions: string[]
    profileType?: string
    activeProfileId?: string
    userType: string
    contextType: string
    organizationSlug?: string
    branchSlug?: string
  }> {
    // STEP 1: Fetch sidebar menu first (before UI permissions) - only if not skipping storage
    // During login flow, sidebar menu is fetched separately, so we skip this
    if (!skipStorage) {
      try {
        await navigationService.getSidebarMenu() // Fetch latest menu
      } catch (sidebarError) {
        // Don't fail if sidebar fetch fails, just log it
      }
    }

    // STEP 2: Fetch UI permissions
    // If contextToken is provided, use it directly; otherwise rely on axios interceptor
    const config = contextToken 
      ? { headers: { Authorization: `Bearer ${contextToken}` } }
      : {}
    
    const response = await api.post(API_ENDPOINTS.AUTH.UI_PERMISSIONS, {}, config)
    
    // API returns: { success: true, data: { permissions: [...], profileType, ... } }
    const permissionsData = response.data.data

    if (!permissionsData || !permissionsData.permissions) {
      throw new Error('No UI permissions received from server')
    }

    // Store permissions in Redux only if not skipping storage
    if (!skipStorage) {
      // Dispatch to Redux store with permissions
      try {
        store.dispatch(setPermissions(permissionsData))
      } catch (error) {
        throw error // Fail fast - don't silently swallow errors
      }
    }

    return permissionsData
  }

  /**
   * Workspace Bootstrap - Unified endpoint for post-login data
   * Replaces separate calls to getContextData, getSidebarMenu, and getUIPermissions
   * 
   * Performance:
   * - First request: 150-250ms (optimized single query)
   * - Cached requests: <60ms (Redis cache)
   * - Single network request instead of 3 sequential calls
   * 
   * @returns Combined context, sidebar, and permissions data (NEW OPTIMIZED STRUCTURE)
   */
  async getWorkspaceBootstrap(): Promise<{
    requiresOnboarding?: boolean
    user?: { id: string; email?: string; name?: string } // TASK 1.3 — User identity for validation
    context: {
      accessibleDomains: Array<{
        organizationId: string
        organizationName: string
        organizationSlug: string
        branchId?: string
        branchName?: string
        branchSlug?: string
        contextType: 'organization' | 'branch'
      }>
      activeBranchId: string // Renamed from active_branch_id
      roles: { [contextId: string]: string } // Role per context (org or branch)
      groups: { [contextId: string]: string[] } // Groups/role names per context
      permissions: string[] // Permission keys for active context (ERP: frontend builds menus from these)
      platformMenuKeys?: string[] // Platform product visibility (RBAC only)
      profiles?: { primary: string[]; functional: string[] }
      workspaces?: string[]
      contextType: 'platform' | 'organization' | 'branch'
      organizationId?: string
      branchId?: string
      organizationSlug?: string
      branchSlug?: string
      profileType?: IdentityProfileType
      activeProfileId?: string
      contextOwners?: Array<{
        fullName: string
        email: string | null
        profilePhotoUrl: string | null
        /** Display role from employee profile (e.g. Teacher, Staff) or branch role; matches My Profile page. */
        role?: string
      }>
      linkedStudents?: Array<{ id: string; firstName?: string; lastName?: string }>
      availableProfilesByBranch?: Record<string, Array<{ profileType: string; profileId?: string }>>
      linkedStudentsByBranch?: Record<string, Array<{ studentProfileId: string; firstName?: string; lastName?: string; class?: string; section?: string }>>
      branding?: {
        resolvedLogo: string | null
        resolvedLogoDark: string | null
        platformLogo: string | null
        platformLogoDark: string | null
        orgLogo: string | null
        orgLogoDark: string | null
        branchLogo: string | null
        branchLogoDark: string | null
      }
    }
  }> {
    const response = await api.get(API_ENDPOINTS.AUTH.WORKSPACE_BOOTSTRAP, {
      timeout: 30000,
    })
    const responseData = response.data?.data || response.data
    // TASK 1.3 — Validate response structure includes user identity
    if (!responseData?.context || !Array.isArray(responseData.context.permissions)) {
      throw new Error('Invalid bootstrap response structure')
    }
    // Backend MUST include user.id for identity validation
    if (!responseData?.user?.id) {
      throw new Error('Invalid bootstrap response: missing user identity')
    }
    return responseData
  }

  /**
   * Get current access token
   * Note: accessToken is now in httpOnly cookie, not accessible from JavaScript
   * Returns null since we can't read it, but the browser sends it automatically
   */
  getAccessToken(): string | null {
    // accessToken is in httpOnly cookie, not accessible via JavaScript
    return null
  }

  /**
   * Get current refresh token
   * Note: refreshToken is now in httpOnly cookie, not accessible from JavaScript
   */
  getRefreshToken(): string | null {
    return null;
  }

  /**
   * Get current user
   */
  getUser(): User | null {
    // Try sessionStorage first
    const userStr = sessionStorage.getItem(this.USER_KEY)
    if (userStr) {
      return JSON.parse(userStr)
    }
    
    // Migration: check localStorage
    try {
      const localStorageUserStr = localStorage.getItem(this.USER_KEY)
      if (localStorageUserStr) {
        const user = JSON.parse(localStorageUserStr)
        // Migrate to sessionStorage
        sessionStorage.setItem(this.USER_KEY, localStorageUserStr)
        // Remove from localStorage
        localStorage.removeItem(this.USER_KEY)
        return user
      }
    } catch (migrationError) {
      // Silent error handling
    }
    
    return null
  }

  /**
   * Get current context
   */
  getContext(): any {
    // Try sessionStorage first
    const contextStr = sessionStorage.getItem(this.CONTEXT_KEY)
    if (contextStr) {
      return JSON.parse(contextStr)
    }
    
    // Migration: check localStorage
    try {
      const localStorageContextStr = localStorage.getItem(this.CONTEXT_KEY)
      if (localStorageContextStr) {
        const context = JSON.parse(localStorageContextStr)
        // Migrate to sessionStorage
        sessionStorage.setItem(this.CONTEXT_KEY, localStorageContextStr)
        // Remove from localStorage
        localStorage.removeItem(this.CONTEXT_KEY)
        return context
      }
    } catch (migrationError) {
      // Silent error handling
    }
    
    return null
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getAccessToken()
  }

  /**
   * Check if user has specific role
   */
  hasRole(roleName: string): boolean {
    const user = this.getUser()
    if (!user || !user.roles) return false

    return user.roles.some((role: any) => role.name === roleName)
  }

  /**
   * Set contexts array in storage
   */
  private setContexts(contexts: UserContext[]): void {
    sessionStorage.setItem('contexts', JSON.stringify(contexts))
    
    // Migration cleanup: remove from localStorage if exists
    try {
      localStorage.removeItem('contexts')
    } catch (migrationError) {
      // Silent error handling
    }
  }

  /**
   * Clear all auth data and everything from storage
   * On logout, we clear everything to ensure a clean state
   */
  private clearAuth(): void {
    // Clear axios Authorization header (static api — session-recovery uses dynamic auth import to avoid axios↔auth cycle)
    try {
      delete api.defaults.headers.common['Authorization']
    } catch {
      // ignore axios cleanup failures
    }

    // Reset Redux slices first so stale UI state is dropped immediately.
    try {
      store.dispatch(resetAuthState())
      store.dispatch(resetPermissions())
      store.dispatch(resetNavigation())
    } catch {
      // ignore store reset failures
    }

    // Clear nav cache artifacts that can leak across users.
    try {
      navigationService.clearStoredSidebarMenu()
    } catch {
      // ignore cache cleanup failures
    }

    // Explicitly clear bootstrap/session flags used by auth initialization.
    try {
      sessionStorage.removeItem('skipNextWorkspaceBootstrap')
      sessionStorage.removeItem('skipBootstrapForToken')
      sessionStorage.removeItem('contextSelectionInProgress')
      sessionStorage.removeItem('logoutInProgress')
      sessionStorage.removeItem('logoutApiCalled')
    } catch {
      // ignore flag cleanup failures
    }

    // TASK 3 — Clear all tokens using tokenManager
    tokenManager.removeToken('contextToken')
    tokenManager.clearAllAuthData()

    // TASK 3 — FULL RESET: Clear both sessionStorage and localStorage
    try {
      sessionStorage.clear()
    } catch (error) {
      // ignore sessionStorage cleanup failures
    }

    try {
      localStorage.clear()
    } catch {
      // ignore localStorage cleanup failures
    }

    // Full theme authority reset so next user or re-login gets backend-driven theme
    clearStoredUIPreferences()
    try {
      localStorage.removeItem('vidhyantra-theme-mode')
      localStorage.removeItem('vidhyantra-text-size')
      localStorage.removeItem('vidhyantra_ui_preferences')
    } catch {
      // ignore
    }
  }
}

export const authService = new AuthService()
export default authService

